import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import UsersList from "../components/UsersList";
import MoviesList from "../components/MoviesList";
import AnimeManagement from "../components/AnimeManagement";
import SeriesManagement from "../components/SeriesManagement";
import PlansAdminSectionCards from "../components/PlansAdminSectionCards";
import UsersWhoWantToSubscribe from "../components/UsersWhoWantToSubscribe";
import UsersReferralsPanel from "../components/UsersReferralsPanel";
import UsersDevicesPanel from "../components/UsersDevicesPanel";
import PlaybackStatsPanel from "../components/PlaybackStatsPanel";
import RecommendationsPanel from "../components/RecommendationsPanel";
import { useSelector } from "react-redux";
import { Dot, ArrowLeft, User, Film, CreditCard, Bell, Share, MonitorSmartphone, PlayCircle, MessageSquare, Tv } from "lucide-react";

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("users");
  const navigate = useNavigate();
  const users = useSelector((state) => state.users.users);
  const user = useSelector((state) => state.auth.user);
  const loggedOut = useSelector((state) => state.auth.loggedOut);
  const isAuthenticated = !!localStorage.getItem("token");

  // Efecto para verificar autenticación y rol de admin
  useEffect(() => {
    // Si el usuario no está autenticado o se ha cerrado sesión, redirigir a inicio
    if (!isAuthenticated || loggedOut) {
      navigate("/home");
      return;
    }

    // Si hay información de usuario y no es admin, redirigir a inicio
    if (user && user.role !== "admin") {
      navigate("/home");
    }
  }, [isAuthenticated, user, loggedOut, navigate]);

  return (
    <div
      className="bg-gray-900 text-white min-h-screen flex flex-col items-center font-montserrat p-4 md:p-8 pt-24 sm:pt-28 md:pt-32"
      style={{
        position: "relative",
        background: "linear-gradient(to bottom, #1a1a1a 0%, #0d0d0d 100%)",
        overflow: "hidden",
      }}
    >
      {/* Barras decorativas de fondo */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `
            repeating-linear-gradient(
              90deg, 
              transparent 0px, 
              transparent 20px, 
              #000000 20px, 
              #000000 40px, 
              transparent 40px, 
              transparent 100px
            )
          `,
          backgroundSize: "100px 100%",
          boxShadow: "inset 0 0 10px rgba(0,0,0,0.8)",
        }}
      />

      {/* Sombra para las barras */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `
            repeating-linear-gradient(
              90deg, 
              transparent 0px, 
              transparent 20px, 
              rgba(0,0,0,0.5) 20px, 
              rgba(0,0,0,0.5) 40px, 
              transparent 40px, 
              transparent 100px
            )
          `,
          backgroundSize: "100px 100%",
          filter: "blur(3px)",
          transform: "translateX(2px)",
        }}
      />

      {/* Contenedor principal */}
      <div
        className="p-6 sm:p-8 md:p-10 rounded-2xl w-full max-w-6xl relative z-10"
        style={{
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 0 30px rgba(0, 0, 0, 0.5)",
          background: "rgba(20, 20, 20, 0.7)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Botón de regreso */}
        <Link
          to="/home"
          className="absolute top-4 right-4 flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-all duration-300"
        >
          <ArrowLeft size={18} />
          Volver al inicio
        </Link>

        {/* Título */}
        <h1 className="text-3xl font-bold mb-8 text-center bg-white bg-clip-text text-transparent">
          Panel de Administración
        </h1>

        {/* Pestañas de navegación */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          <button
            onClick={() => setActiveTab("users")}
            className={`flex items-center gap-2 py-3 px-6 rounded-full transition-all duration-300 ${activeTab === "users"
                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/30"
                : "bg-gray-800 hover:bg-gray-700 text-gray-300"
              }`}
          >
            <User size={18} />
            Usuarios
          </button>

          <button
            onClick={() => setActiveTab("movies")}
            className={`flex items-center gap-2 py-3 px-6 rounded-full transition-all duration-300 ${activeTab === "movies"
                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/30"
                : "bg-gray-800 hover:bg-gray-700 text-gray-300"
              }`}
          >
            <Film size={18} />
            Películas
          </button>

          <button
            onClick={() => setActiveTab("series")}
            className={`flex items-center gap-2 py-3 px-6 rounded-full transition-all duration-300 ${activeTab === "series"
                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/30"
                : "bg-gray-800 hover:bg-gray-700 text-gray-300"
              }`}
          >
            <Tv size={18} />
            Series
          </button>

          <button
            onClick={() => setActiveTab("animes")}
            className={`flex items-center gap-2 py-3 px-6 rounded-full transition-all duration-300 ${activeTab === "animes"
                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/30"
                : "bg-gray-800 hover:bg-gray-700 text-gray-300"
              }`}
          >
            <Tv size={18} />
            Animes
          </button>

          <button
            onClick={() => setActiveTab("plans")}
            className={`flex items-center gap-2 py-3 px-6 rounded-full transition-all duration-300 ${activeTab === "plans"
                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/30"
                : "bg-gray-800 hover:bg-gray-700 text-gray-300"
              }`}
          >
            <CreditCard size={18} />
            Planes
          </button>

          <button
            onClick={() => setActiveTab("devices")}
            className={`flex items-center gap-2 py-3 px-6 rounded-full transition-all duration-300 ${activeTab === "devices"
                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/30"
                : "bg-gray-800 hover:bg-gray-700 text-gray-300"
              }`}
          >
            <MonitorSmartphone size={18} />
            Dispositivos
          </button>

          <button
            onClick={() => setActiveTab("playbackStats")}
            className={`flex items-center gap-2 py-3 px-6 rounded-full transition-all duration-300 ${activeTab === "playbackStats"
                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/30"
                : "bg-gray-800 hover:bg-gray-700 text-gray-300"
              }`}
          >
            <PlayCircle size={18} />
            Reproducciones
          </button>

          <button
            onClick={() => setActiveTab("willSubscribers")}
            className={`flex items-center gap-2 py-3 px-6 rounded-full transition-all duration-300 relative ${activeTab === "willSubscribers"
                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/30"
                : "bg-gray-800 hover:bg-gray-700 text-gray-300"
              }`}
          >
            <Bell size={18} />
            Solicitudes
            {users.some((userData) => userData.willSubscribe) && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse"></span>
            )}
          </button>

          <button
            onClick={() => setActiveTab("recommendations")}
            className={`flex items-center gap-2 py-3 px-6 rounded-full transition-all duration-300 ${activeTab === "recommendations"
                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/30"
                : "bg-gray-800 hover:bg-gray-700 text-gray-300"
              }`}
          >
            <MessageSquare size={18} />
            Recomendaciones
          </button>
        </div>

        {/* Contenido de las pestañas */}
        <div className="mt-8">
          {activeTab === "users" && <UsersList />}
          {activeTab === "movies" && <MoviesList />}
          {activeTab === "series" && <SeriesManagement />}
          {activeTab === "animes" && <AnimeManagement />}
          {activeTab === "plans" && <PlansAdminSectionCards />}
          {activeTab === "referrals" && <UsersReferralsPanel />}
          {activeTab === "devices" && <UsersDevicesPanel />}
          {activeTab === "playbackStats" && <PlaybackStatsPanel />}
          {activeTab === "willSubscribers" && <UsersWhoWantToSubscribe />}
          {activeTab === "recommendations" && <RecommendationsPanel />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;